/**
 * Indicadores de una versión.
 *
 * Un indicador vive EN UNA VERSIÓN y se ancla a un aspecto del árbol. Un aspecto
 * está activo en una versión solo si tiene indicador en ella — de ahí que crear
 * o borrar un indicador cambie qué se ve en la estructura de esa versión.
 *
 * Solo editable con la VERSIÓN en estado `edicion`.
 *
 * Store Pinia: estado compartido del módulo y orquestación de sus servicios.
 * Las vistas no llaman a `http` ni a los servicios directamente; pasan por aquí.
 * Los servicios no lanzan, así que las acciones interpretan valores neutros
 * (`[]`, `null`, `false`) como fallo.
 */

import { defineStore } from 'pinia'
import type Indicador from '@/interfaces/Indicador'
import {getIndicadoresService, getIndicadorByIdService, crearIndicadorService, eliminarIndicadorService, getIndicadoresPorComponenteService, updateIndicadorService} from '@/services/IndicadoresService'

/** Store `indicadores`. Ver la cabecera del archivo para el contrato del módulo. */
export const useGestionIndicadores = defineStore('indicadores', {
  state: () => ({
    indicadores: [] as Indicador[],
    indicadorActual: null as Indicador | null,
    urlIndicadores: `${process.env.VUE_APP_API_BASE_URL}/api/indicadores`,
    urlComponentes: `${process.env.VUE_APP_API_BASE_URL}/api/componentes`
  }),

  actions: {
    async cargarTodos(): Promise<void> {
      const lista = await getIndicadoresService(this.urlIndicadores)
      this.indicadores = Array.isArray(lista) ? lista : []
    },

    async cargarPorId(id_indicador: number): Promise<Indicador | null> {
      const indicador = await getIndicadorByIdService(this.urlIndicadores, id_indicador)
      this.indicadorActual = indicador
      return indicador
    },

    async cargarPorComponente(id_componente: number): Promise<Indicador[]> {
      const lista = await getIndicadoresPorComponenteService(this.urlComponentes, id_componente)
      return Array.isArray(lista) ? lista : []
    },

    // Crear indicador
    async crear(
      id_componente: number,
      data: {
        id_version: number
        id_tipo_componente: number
        enunciado: string
        formula?: string | null
        unidades?: string | null
        fuente_informacion?: string | null
        descripcion?: string | null
        peso_opinion?: number | null
      }
    ): Promise<Indicador | null> {
      const result = await crearIndicadorService(this.urlComponentes, id_componente, data)
      if (result.ok && result.data) {
        this.indicadores.push(result.data)
        this.indicadorActual = result.data
        return result.data
      }
      if (!result.ok) {
        console.warn('[Store] Error al crear indicador:', result.message)
      }
      return null
    },

    // Eliminar indicador — el servicio devuelve { ok, message }
    async eliminar(id_indicador: number): Promise<boolean> {
      const result = await eliminarIndicadorService(this.urlIndicadores, id_indicador)
      if (result.ok) {
        this.indicadores = this.indicadores.filter(i => i.id_indicador !== id_indicador)
        if (this.indicadorActual?.id_indicador === id_indicador) {
          this.indicadorActual = null
        }
      } else {
        console.warn('[Store] Error al eliminar indicador:', result.message)
      }
      return result.ok
    },

    // Actualizar indicador — el servicio devuelve { ok, data?, message }
    async actualizarIndicador(
      id_indicador: number,
      payload: Partial<Pick<Indicador, 'enunciado' | 'formula' | 'unidades' | 'fuente_informacion' | 'descripcion' | 'peso_opinion'>>
    ): Promise<Indicador | null> {
      const result = await updateIndicadorService(this.urlIndicadores, id_indicador, payload)
      if (result.ok && result.data) {
        const idx = this.indicadores.findIndex(i => i.id_indicador === id_indicador)
        if (idx !== -1) this.indicadores.splice(idx, 1, result.data)
        this.indicadorActual = result.data
        return result.data
      }
      if (!result.ok) {
        console.warn('[Store] Error al actualizar indicador:', result.message)
      }
      return null
    }
  }
})
